// MilestoneCard — the Today-screen surface for the F8 finite milestone
// set. Fetches /api/milestones (server-side computed + lazy-unlocked),
// shows the highest-rank unlocked milestone's copy, and a small
// "next milestone" hint with progress.
//
// Compact by design: Today is crowded. One small card with the title +
// body of the most recently relevant unlock + a single "next: X" hint.
// The dismiss affordance writes to localStorage so a user who has seen
// the unlock once does not see it re-celebrated on every load.
//
// Hidden entirely if (a) no plan exists (early-onboarding users) or
// (b) no milestones are unlocked yet AND the user has no progress to
// hint at. The first condition is the most important — we don't want to
// surface a celebration card for someone who hasn't even set a baseline.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { useApi } from '../../hooks/useApi';
import { apiGet } from '../../lib/api';
import { MILESTONES, pickHighlight, pickNext } from '../../lib/milestones';
import type { MilestonesResponse, TaperPlanWire, StreakInfo } from '@shared/types';

// localStorage key for the milestone the user has already dismissed.
// We store the key (not a rank) so adding a new milestone to the catalog
// in a future release doesn't cause every old user to see it "for the
// first time again". Only the most recently dismissed key is tracked
// because the card only ever surfaces one at a time.
const DISMISSED_KEY = 'qk.milestones.dismissed.v2';

function readDismissed(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(DISMISSED_KEY);
  } catch {
    return null;
  }
}

function writeDismissed(key: string): void {
  try {
    window.localStorage.setItem(DISMISSED_KEY, key);
  } catch {
    // private mode / quota — best effort
  }
}

interface MilestoneCardProps {
  // Props preserved for compatibility with TodayScreen's existing call
  // site. plan is used to gate visibility (no plan → no card). streak
  // is no longer needed (server-computed) but kept on the surface so
  // the parent doesn't have to change.
  streak: StreakInfo | null;
  plan: TaperPlanWire | null;
}

export function MilestoneCard({ plan }: MilestoneCardProps) {
  // No plan → user is pre-onboarding. Suppress the card entirely so we
  // don't celebrate streaks before the user has set a baseline.
  const skip = plan == null;
  const res = useApi<MilestonesResponse>(skip ? null : '/api/milestones');
  const data = res.status === 'ok' ? res.data : null;

  const highlight = useMemo(() => {
    if (!data) return null;
    return pickHighlight(data.unlocked.map((u) => u.key));
  }, [data]);

  const next = useMemo(() => {
    if (!data) return null;
    return pickNext(data.progress, data.unlocked.map((u) => u.key));
  }, [data]);

  const [dismissed, setDismissed] = useState<string | null>(readDismissed());

  // Phase 5 PR-C — surface a "View the wall" link when the user is in
  // the milestone_wall_v1 rollout. The probe response is cached by the
  // SPA's useApi hook for the session, so the extra request cost is
  // negligible. Off-rollout users see the existing card unchanged.
  const [wallEnabled, setWallEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    apiGet<{ enabled: boolean }>('/api/community/milestones?probe=1')
      .then((r) => {
        if (!cancelled) setWallEnabled(r.enabled === true);
      })
      .catch(() => {
        if (!cancelled) setWallEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (skip) return null;
  if (!data) return null;
  // Nothing unlocked and no next milestone available → hide entirely
  // (likely means the user has unlocked every milestone OR they have
  // no progress yet to hint at).
  if (!highlight && !next) return null;

  const shouldShowHighlight = highlight && highlight.key !== dismissed;

  function dismiss(): void {
    if (highlight) {
      writeDismissed(highlight.key);
      setDismissed(highlight.key);
    }
  }

  return (
    <Card>
      {shouldShowHighlight ? (
        <div
          className="row"
          style={{
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 'var(--space-3)',
          }}
        >
          <div style={{ flex: 1 }}>
            <h3 className="text-h3" style={{ marginBlockEnd: 'var(--space-1)' }}>
              {highlight.title}
            </h3>
            <p style={{ margin: 0 }}>{highlight.body}</p>
            {next ? (
              <p
                className="muted text-caption"
                style={{ margin: 0, marginBlockStart: 'var(--space-2)' }}
              >
                {nextHintLine(next.def.nextLabel, next.current, next.def.threshold)}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            aria-label="Dismiss milestone"
            onClick={dismiss}
            className="link-button"
            style={{ alignSelf: 'flex-start' }}
          >
            ×
          </button>
        </div>
      ) : (
        // No unlock to celebrate right now — show the next-milestone
        // hint with a heading so the card has identity at a glance
        // instead of reading as orphan text.
        next ? (
          <>
            <h3 className="text-h3" style={{ marginBlockEnd: 'var(--space-1)' }}>
              Next milestone
            </h3>
            <p style={{ margin: 0 }}>{next.def.nextLabel}</p>
            <p
              className="muted text-caption"
              style={{ margin: 0, marginBlockStart: 'var(--space-2)' }}
            >
              {progressHintLine(next.def.group, next.current, next.def.threshold)}
            </p>
          </>
        ) : null
      )}
      {shouldShowHighlight ? (
        <div style={{ marginBlockStart: 'var(--space-3)' }}>
          <Button variant="secondary" size="md" onClick={dismiss}>
            Got it
          </Button>
        </div>
      ) : null}
      {wallEnabled ? (
        <div style={{ marginBlockStart: 'var(--space-2)' }}>
          <Link to="/app/community/milestones">View the wall →</Link>
        </div>
      ) : null}
    </Card>
  );
}

// "20 / 30 days · next: 30-day streak" style hint. We avoid promising a
// specific date because the user's behavior can change before they hit
// it; the hint is descriptive, not a commitment.
function nextHintLine(label: string, current: number, threshold: number): string {
  return `${current} / ${threshold} · next: ${label}`;
}

// Unit-aware progress hint used by the no-unlock-yet branch where the
// heading already conveys "next milestone" so we only need the count.
function progressHintLine(
  group: 'streak' | 'reduction' | 'dose-free',
  current: number,
  threshold: number,
): string {
  if (group === 'reduction') return `${current}% / ${threshold}%`;
  return `${current} / ${threshold} days`;
}

// ---------------------------------------------------------------------
// Re-export the milestone catalog for tests / future callers so this
// component stays the single canonical reference for "what the Today
// surface celebrates". The pure threshold/copy table lives in
// src/lib/milestones.ts — see that module for the rules.
// ---------------------------------------------------------------------
export { MILESTONES };
